<script setup lang="ts">
// Vue3 の基本機能やライブラリのインポート
import { ref, computed, onMounted, watch, onUnmounted } from 'vue'
import { Carousel, Navigation, Pagination, Slide } from 'vue3-carousel'
import { Radar } from 'vue-chartjs'
import { Chart as ChartJS, RadialLinearScale, PointElement, LineElement, Filler, Tooltip, Legend } from 'chart.js'
import 'vue3-carousel/dist/carousel.css'
import type { SiteDetail, TableItem, AdDetail as AdDetailType, ClickableItem } from '@/types'

// デフォルトのテーブル項目設定
const defaultTableItems: TableItem[] = [
  { 
    key: "summary.score", 
    label: "おすすめ度<br>(10点満点)", 
    value_type: "score",
    score_display: "number",
    mobile_label: "おすすめ度"
  },
  { 
    key: "summary.regist_style", 
    label: "会員登録方法", 
    value_type: "text"
  },
  { 
    key: "summary.accomplishments", 
    label: "実績", 
    value_type: "accomplishments"
  },
  { 
    key: "summary.fee", 
    label: "入会金・年会費", 
    value_type: "icon",
    sub_key: "index",
    mobile_label: "入会金"
  },
  { 
    key: "summary.last_result", 
    label: "直近の高配当実績", 
    value_type: "icon",
    sub_key: "index",
    mobile_label: "高配当実績"
  },
  { 
    key: "summary.free_tier", 
    label: "無料情報数", 
    value_type: "icon",
    sub_key: "index"
  },
  { 
    key: "summary.predictor", 
    label: "予想師の充実度", 
    value_type: "icon",
    sub_key: "index",
    mobile_label: "予想師"
  },
  { 
    key: "summary.benefits", 
    label: "入会特典", 
    value_type: "icon",
    sub_key: "index"
  },
  { 
    key: "url", 
    label: "公式サイト", 
    value_type: "button",
    button_text: "公式サイト"
  }
];

// プロパティ定義
const { adDetail, groupId } = defineProps<{
  adDetail: AdDetailType;
  groupId: string;
}>()

// 外観の設定（デフォルト 'default' を設定）
const appearance = ref(adDetail.appearance || 'default')

// スクロール状態を監視する変数
const hasScrolled = ref(false),
      hasScrolled100vh = ref(false),
      currentSection = ref('header'),
      isAtBottom = ref(false)

// セクション要素の参照を保持
const headerRef = ref<HTMLElement | null>(null),
      tableRef = ref<HTMLElement | null>(null),
      siteDetailRefs = ref<HTMLElement[]>([])

// スクロールイベントハンドラ
const handleScroll = (): void => {
  // スクロール位置
  const scrollY = window.scrollY,
        windowHeight = window.innerHeight,
        documentHeight = document.documentElement.scrollHeight
  
  // 1pxでもスクロールしたかどうか
  hasScrolled.value = scrollY > 0
  
  // 100vh分スクロールしたかどうか
  hasScrolled100vh.value = scrollY > windowHeight
  
  // 最下部に到達したかどうか (下部から20px以内)
  isAtBottom.value = scrollY + windowHeight >= documentHeight - 20
  
  // 現在表示されているセクションを判定
  determineCurrentSection(scrollY, windowHeight)
}

// 現在表示されているセクションを判定する関数
const determineCurrentSection = (scrollY: number, windowHeight: number): void => {
  const headerEl = headerRef.value,
        tableEl = tableRef.value,
        detailEls = siteDetailRefs.value
  
  // 要素の位置情報を取得
  if (headerEl && tableEl && detailEls.length > 0) {
    // 各セクションの位置と高さを取得
    const headerRect = headerEl.getBoundingClientRect(),
          tableRect = tableEl.getBoundingClientRect(),
          detailRects = detailEls.map(el => el.getBoundingClientRect())
    
    // ビューポートの中心位置
    const viewportCenter = windowHeight / 2
    
    // ヘッダー部分が中心に近いか
    if (headerRect.top <= viewportCenter && headerRect.bottom >= viewportCenter) {
      currentSection.value = 'header'
      return
    }
    
    // テーブル部分が中心に近いか
    if (tableRect.top <= viewportCenter && tableRect.bottom >= viewportCenter) {
      currentSection.value = 'table'
      return
    }
    
    // 各サービス詳細が中心に近いか
    for (let i = 0; i < detailRects.length; i++) {
      const rect = detailRects[i]
      if (rect && rect.top <= viewportCenter && rect.bottom >= viewportCenter) {
        currentSection.value = `site-detail-${i}`
        return
      }
    }
    
    // どのセクションにも当てはまらない場合
    currentSection.value = 'unknown'
  }
}

/**
 * 数字にカンマを付与する関数
 * @param num 数字
 * @returns カンマ区切りの文字列
 */
const addComma = (num: number) => {
  return num.toString().replace(/(\d)(?=(\d{3})+$)/g, '$1,')
}

/**
 * 指定インデックスに対応する評価アイコン（HTML文字列）を返す関数
 * @param index 0～3 の数値
 * @returns 評価アイコンのHTML文字列
 */
const indexIcon = (index: number) => {
  const results = [
    "bad'>×",
    "normal'>△",
    "good'>○",
    "great'>◎"
  ]
  return `<span class='ui-scoring-icon ui-icon--${results[index]}</span>`;
}

// Chart.js の各コンポーネントを登録
ChartJS.register(RadialLinearScale, PointElement, LineElement, Filler, Tooltip, Legend)

// サンプルのレーダーチャートデータ（必要に応じて adDetail の値を使用してください）
const scoreChartData = ref({
  labels: ['1', '2', '3', '4', '5', '6', '7', '8', '9', '10'],
  datasets: [
    {
      label: 'Score',
      data: [2, 4, 6, 8, 10, 9, 7, 5, 3, 1],
      borderColor: 'red',
      backgroundColor: 'rgba(255, 0, 0, 0.2)',
    },
  ],
})

// チャートのオプション設定
const scoreChartOptions = ref({
  scales: {
    r: {
      beginAtZero: true,
      max: 10,
    },
  },
})

/**
 * テーブルヘッダーの表示形式を決定する算出プロパティ
 * custom.table_type があればその値を返す
 */
const tableHeadingStyle = computed((): string | undefined => {
  if (adDetail.custom?.table_type) {
    return adDetail.custom.table_type;
  }
  return undefined;
})

/**
 * テーブル設定を取得する算出プロパティ
 */
const tableItems = computed<TableItem[]>(() => {
  // 通常のtable_settings処理
  if (adDetail.table_settings?.items) {
    if (adDetail.table_settings.default_items) {
      // カスタム項目とデフォルト項目を結合
      return [...adDetail.table_settings.items, ...defaultTableItems];
    }
    // カスタム項目のみを使用
    return adDetail.table_settings.items;
  }
  
  // table_settingsがない場合
  // デフォルト項目をコピー（どのケースでも変更できるようにするため）
  const compatItems = [...defaultTableItems];
  
  // custom.detail.score_style が "star" の場合の互換性対応
  if (adDetail.custom?.detail?.score_style === 'star') {
    const scoreIndex = compatItems.findIndex(item => item.key === 'summary.score');
    if (scoreIndex !== -1 && compatItems[scoreIndex]) {
      const item = compatItems[scoreIndex];
      if (item) {
        compatItems[scoreIndex] = {
          ...item,
          score_display: 'both',
          key: item.key,
          label: item.label
        };
      }
    }
  }
  
  // 修正したデフォルト項目を返す
  return compatItems;
});

/**
 * オブジェクトからドット記法のパスで値を取得する関数
 * @param obj 対象オブジェクト
 * @param path ドット記法のパス (例: "summary.fee.index")
 * @returns 取得した値
 */
const getValueByPath = <T extends Record<string, any>>(obj: T, path: string): any => {
  if (!obj || !path) return null;
  
  return path.split('.').reduce((prev, curr) => {
    return prev && typeof prev === 'object' ? prev[curr] : null;
  }, obj as any);
};

/**
 * テーブル項目の値を取得する関数
 * @param site サイト情報オブジェクト
 * @param item テーブル項目設定
 * @returns 表示用の値
 */
const getItemValue = (site: SiteDetail, item: TableItem): any => {
  const value = getValueByPath(site, item.key);
  
  if (item.sub_key && value && typeof value === 'object') {
    return value[item.sub_key];
  }
  
  return value;
};

/**
 * テーブル項目の表示内容を取得する関数
 * @param site サイト情報オブジェクト
 * @param item テーブル項目設定
 * @returns HTMLやテキスト
 */
const renderItemContent = (site: SiteDetail, item: TableItem): string => {
  const value = getItemValue(site, item);
  
  switch (item.value_type) {
    case 'icon':
      if (item.sub_key === 'index') {
        const label = getValueByPath(site, `${item.key}.label`);
        return `
          <div>${indexIcon(value)}</div>
          <div>${label || ''}</div>
        `;
      }
      return value?.toString() || '';
      
    case 'score':
      // スコア表示タイプに応じた出力
      const scoreDisplay = item.score_display || 'number'; // デフォルトは数値のみ
      
      if (scoreDisplay === 'stars') {
        return `<div class="stars">${getStars(value)}</div>`;
      } else if (scoreDisplay === 'both') {
        return `
          <div class="stars">${getStars(value)}</div>
          ${value}
        `;
      } else { // number
        return `${value}`;
      }
      
    case 'accomplishments':
      return `
        ${value.accuracy}<br>
        ${value.recovery_rate}
      `;
      
    case 'button':
      return `<a class="ui-button--jump" href="${site.url}" target="_blank">${item.button_text || '詳細'}</a>`;
      
    case 'text':
    default:
      return value;
  }
};

/**
 * テーブル項目のCSSクラスを取得する関数
 * @param site サイト情報オブジェクト
 * @param item テーブル項目設定
 * @returns CSSクラス名の配列
 */
const getItemClasses = (site: any, item: TableItem) => {
  const classes = [];

  // 
  if (site.is_prime) classes.push('prime');
  
  // 
  if (item.value_type === 'score') classes.push('score');
  
  // 
  if (item.value_type === 'accomplishments') classes.push('accomplishments');
  
  // 
  if (item.custom_class) classes.push(item.custom_class);
  
  return classes.join(' ');
};

/**
 * テキスト内の置換トークンを処理する関数
 * - {{source}} は adDetail.assetPath に置換される
 * - {{siteurl[number]}} は adDetail.sites[number].url に置換される
 *
 * @param text 置換対象の文字列
 * @returns 置換後の文字列
 */
const replaceTokens = (text: string): string => {
  if (!text) return text;
  return text
    .replace(/{{source}}/g, adDetail.assetPath || '')
    .replace(/{{siteurl\[(\d+)\]}}/g, (match, indexStr) => {
      const index = parseInt(indexStr, 10);
      // sites配列が存在しindexが有効範囲内、かつurlが存在する場合のみ置換
      if (adDetail.sites && 
          index >= 0 && 
          index < adDetail.sites.length) {
        const site = adDetail.sites[index];
        if (site && typeof site.url === 'string') {
          return site.url;
        }
      }
      return match;
    });
};

/**
 * カスタムCSSを適用する関数
 * @param customCss テンプレート化されたCSS文字列
 * @param assetPath CSS内の {{source}} を置換するためのパス
 */
const applyCustomCss = (customCss: string | undefined, assetPath: string | undefined) => {
  if (customCss && assetPath) {
    // replaceTokens を利用して {{source}} と {{siteurl[number]}} の両方を置換する
    const css = replaceTokens(customCss);
    const linkEl = document.createElement('link');
    linkEl.rel = 'stylesheet';
    linkEl.href = css;
    document.getElementById('style')?.appendChild(linkEl);
  }
}

/**
 * クリッカブルエリアがクリックされたときの処理
 * @param item クリックされたエリアの情報
 */
const handleAreaClick = (item: ClickableItem): void => {
  if (item.url) {
    // URLにトークンが含まれている場合は置換する
    const processedUrl = replaceTokens(item.url);
    
    // item.targetが指定されていればそれを使用、なければ_blankをデフォルトとする
    const target = item.target || '_blank';
    window.open(processedUrl, target);
  }
};

// adDetail.custom?.css の変更を監視し、変化があればカスタムCSSを適用する
watch(
  () => adDetail.custom?.css,
  (newCustomCss) => {
    if (newCustomCss) {
      applyCustomCss(newCustomCss, adDetail.assetPath);
    }
  },
  { immediate: true }
)

// コンポーネントマウント時にもカスタムCSSを適用する
onMounted(() => {
  applyCustomCss(adDetail.custom?.css, adDetail.assetPath);
  // スクロールイベントリスナーを登録
  window.addEventListener('scroll', handleScroll)
  // 初期状態の設定
  handleScroll()
})

// コンポーネントのアンマウント時にイベントリスナーを解除
onUnmounted(() => {
  window.removeEventListener('scroll', handleScroll)
})

// コンポーネントクラスを computed で返す
const componentClasses = computed(() => {
  return {
    'is-scrolled': hasScrolled.value,
    'is-scrolled-100vh': hasScrolled100vh.value,
    'is-at-bottom': isAtBottom.value,
    [`section-${currentSection.value}`]: true
  }
})

/**
 * 0〜10のスコアをもとに、半分にして四捨五入した数分の⭐︎（絵文字）を返す関数
 * @param score 数値スコア
 * @returns スターの絵文字文字列
 */
const getStars = (score: number) => {
  const stars = '⭐️'.repeat(Math.round((score / 2) - 0.2));
  return stars;
}
</script>

<template>
  <main :class="[appearance, componentClasses]" v-if="adDetail">
    <!-- ヘッダー部分：リード画像とキャッチコピー -->
    <header ref="headerRef">
      <h1>
        <!-- PC用とモバイル用で異なる画像を表示する場合 -->
        <template v-if="adDetail.lead_image_mobile">
          <!-- PC用画像（クリッカブルマップあり） -->
          <div class="image-container nosp" v-if="adDetail.lead_image_clickable_map">
            <img :src="adDetail.lead_image" :alt="adDetail.sitename">
            <div 
              v-for="(item, index) in adDetail.lead_image_clickable_map" 
              :key="'desktop-clickable-'+index"
              class="clickable-area"
              :style="{
                left: (item.x * 100) + '%',
                top: (item.y * 100) + '%',
                width: (item.width * 100) + '%',
                height: (item.height * 100) + '%'
              }"
              @click="handleAreaClick(item)"
            ></div>
          </div>
          <img v-else :src="adDetail.lead_image" :alt="adDetail.sitename" class="nosp">
          
          <!-- モバイル用画像（クリッカブルマップあり） -->
          <div class="image-container nopc" v-if="adDetail.lead_image_mobile_clickable_map">
            <img :src="adDetail.lead_image_mobile" :alt="adDetail.sitename">
            <div 
              v-for="(item, index) in adDetail.lead_image_mobile_clickable_map" 
              :key="'mobile-clickable-'+index"
              class="clickable-area"
              :style="{
                left: (item.x * 100) + '%',
                top: (item.y * 100) + '%',
                width: (item.width * 100) + '%',
                height: (item.height * 100) + '%'
              }"
              @click="handleAreaClick(item)"
            ></div>
          </div>
          <img v-else :src="adDetail.lead_image_mobile" :alt="adDetail.sitename" class="nopc">
        </template>
        
        <!-- 従来通り、同じ画像を表示する場合 -->
        <template v-else>
          <!-- 単一画像（クリッカブルマップあり） -->
          <div class="image-container" v-if="adDetail.lead_image_clickable_map">
            <img :src="adDetail.lead_image" :alt="adDetail.sitename">
            <div 
              v-for="(item, index) in adDetail.lead_image_clickable_map" 
              :key="'clickable-'+index"
              class="clickable-area"
              :style="{
                left: (item.x * 100) + '%',
                top: (item.y * 100) + '%',
                width: (item.width * 100) + '%',
                height: (item.height * 100) + '%'
              }"
              @click="handleAreaClick(item)"
            ></div>
          </div>
          <img v-else :src="adDetail.lead_image" :alt="adDetail.sitename">
        </template>
      </h1>
      <div class="pr-notice"><span>PR</span></div>
      <!-- adDetail.lead_wordings に含まれるトークンも置換 -->
      <div v-html="replaceTokens(adDetail.lead_wordings)" class="lead-section"></div>
    </header>

    <!-- メインコンテンツ：テーブル・サイト詳細など -->
    <section ref="tableRef">
      <!-- 横並びテーブルの場合 -->
      <template v-if="tableHeadingStyle == 'horizontal'">
        <div class="horizontal-table" :class="[tableHeadingStyle]">
          <div>
            <table>
              <thead>
                <tr>
                  <th></th>
                  <th
                    v-for="(site, i) in adDetail.sites"
                    :key="site.sitename"
                    :class="{ prime: site.is_prime }"
                  >
                    <!-- ランクアイコン（1st～3rd） -->
                    <div v-if="i === 0" class="rank-icon rank-icon-1st"></div>
                    <div v-if="i === 1" class="rank-icon rank-icon-2nd"></div>
                    <div v-if="i === 2" class="rank-icon rank-icon-3rd"></div>
                    <a :href="site.url" target="_blank">
                      <img :src="site.icon_image" :alt="site.sitename">
                    </a>
                    <a class="chip" :href="site.url" target="_blank">{{ site.sitename }}</a>
                  </th>
                </tr>
              </thead>
              <tbody>
                <!-- 動的にテーブル行を生成 -->
                <tr v-for="item in tableItems" :key="item.key">
                  <th>
                    <span class="nopc" v-html="item.mobile_label || item.label"></span>
                    <span class="nosp" v-html="item.label || item.mobile_label"></span>
                  </th>
                  <td
                    v-for="site in adDetail.sites"
                    :key="`${site.sitename}-${item.key}`"
                    :class="getItemClasses(site, item)"
                    v-html="renderItemContent(site, item)"
                  ></td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </template>

      <!-- スクロール可能テーブルの場合 -->
      <template v-else>
        <div class="scrollable-table" :class="[tableHeadingStyle]">
          <div>
            <table>
              <thead>
                <tr>
                  <th
                    v-for="(site, i) in adDetail.sites"
                    :key="site.sitename"
                    :class="{ prime: site.is_prime }"
                  >
                    <div 
                      v-if="i < 3" 
                      class="rank-icon" 
                      :class="`rank-icon-${['1st', '2nd', '3rd'][i]}`"
                    ></div>
                    <a :href="site.url" target="_blank">
                      <img :src="site.icon_image" :alt="site.sitename">
                    </a>
                    <a class="chip" :href="site.url" target="_blank">{{ site.sitename }}</a>
                  </th>
                </tr>
              </thead>
              <tbody>
                <!-- 動的にテーブル行を生成 -->
                <template v-for="(item, itemIndex) in tableItems" :key="item.key">
                  <!-- ラベル行 -->
                  <tr>
                    <th :colspan="adDetail.sites.length">
                      <span class="centering">
                        <span class="table-labeltext nopc" v-html="item.mobile_label || item.label"></span>
                        <span class="table-labeltext nosp" v-html="item.label || item.mobile_label"></span>
                      </span>
                    </th>
                  </tr>
                  <!-- 値行 -->
                  <tr>
                    <td
                      v-for="site in adDetail.sites"
                      :key="`${site.sitename}-${item.key}`"
                      :class="getItemClasses(site, item)"
                      v-html="renderItemContent(site, item)"
                    ></td>
                  </tr>
                </template>
              </tbody>
            </table>
          </div>
        </div>
      </template>
    </section>

    <!-- サイト詳細情報 -->
    <section class="site-detail">
      <article 
        v-for="(site, i) in adDetail.sites"
        :key="site.sitename"
        :ref="(el) => { if (el) siteDetailRefs[i] = el as HTMLElement }"
      >
        <div>
          <h2 class="sitename">
            <div 
                v-if="i < 3" 
                class="rank-icon" 
                :class="`rank-icon-${['1st', '2nd', '3rd'][i]}`"
              ></div>
            {{ site.sitename }}
          </h2>

          <div class="site-detail--lead">
            <div>
              <img :src="site.banner_image" :alt="site.sitename" class="site-detail--lead-banner">
            </div>
            <div>
              <h3 class="first">総合評価</h3>
              <div class="score" v-if="adDetail.custom?.detail?.score_style == 'star'">
                <strong class="stars">{{ getStars(site.summary.score) }}</strong><br>
                <small>{{ site.summary.score }} / 10点</small>
              </div>
              <div class="score" v-else>
                <strong>{{ site.summary.score }}</strong> / 10点
              </div>

              <!-- チャート表示：テーブル形式かレーダーチャートか -->
              <template v-if="adDetail.custom?.detail?.chart_style == 'table'">
                <table class="detail-score-table">
                  <tbody>
                    <tr v-for="(label, i) in site.score_chart.labels" :key="label">
                      <th>{{ label }}</th>
                      <td>{{ getStars(site.score_chart.values[i] || 0) }}</td>
                    </tr>
                  </tbody>
                </table>
              </template>
              <template v-else>
                <Radar
                  :data="{
                    labels: site.score_chart.labels,
                    datasets: [{
                      label: '点数',
                      data: site.score_chart.values,
                      borderColor: 'red',
                      backgroundColor: 'rgba(255, 0, 0, 0.2)'
                    }]
                  }"
                  :options="{
                    scales: {
                      r: { beginAtZero: true, max: 10 }
                    }
                  }"
                />
              </template>
            </div>
          </div>

          <h3 class="first">的中実績</h3>
          <template v-if="adDetail.custom?.detail?.accomplishments_style == 'tiled'">
            <ul class="accomplishment-tile">
              <li v-for="accomplishment in site.accomplishments" :key="accomplishment.date">
                <div class="accomplishment-item">
                  <h4>{{ accomplishment.date }}</h4>
                  <div>
                    <p class="race-name">{{ accomplishment.race_name }}</p>
                    <p class="result">{{ addComma(accomplishment.result) }} <span class="unit">円</span></p>
                  </div>
                </div>
              </li>
            </ul>
          </template>
          <template v-else>
            <!-- Carousel 表示（PC・モバイルで itemsToShow を変える） -->
            <div class="accomplishments nopc">
              <Carousel :wrapAround="true" :itemsToShow="1">
                <Slide v-for="accomplishment in site.accomplishments" :key="accomplishment.date">
                  <div class="accomplishment-item">
                    <h4>{{ accomplishment.date }}</h4>
                    <div>
                      <p class="race-name">{{ accomplishment.race_name }}</p>
                      <p class="result">{{ addComma(accomplishment.result) }} <span class="unit">円</span></p>
                    </div>
                  </div>
                </Slide>
                <template #addons>
                  <Pagination />
                </template>
              </Carousel>
            </div>

            <div class="accomplishments nosp">
              <Carousel :wrapAround="true" :itemsToShow="1.8">
                <Slide v-for="accomplishment in site.accomplishments" :key="accomplishment.date">
                  <div class="accomplishment-item">
                    <h4>{{ accomplishment.date }}</h4>
                    <div>
                      <p class="race-name">{{ accomplishment.race_name }}</p>
                      <p class="result">{{ addComma(accomplishment.result) }}円</p>
                    </div>
                  </div>
                </Slide>
                <template #addons>
                  <Pagination />
                </template>
              </Carousel>
            </div>
          </template>

          <h3 class="second">
            <a :href="site.url" target="_blank">{{ site.sitename }}</a>について
          </h3>
          <div class="salespoints">
            <h4>おすすめポイント</h4>
            <ul>
              <li v-for="point in site.sales_point" :key="point" v-html="replaceTokens(point)"></li>
            </ul>
          </div>

          <h3 class="second">特徴</h3>
          <div class="features">
            <template v-for="feature in site.features" :key="feature.label">
              <h4>■ {{ feature.label }}</h4>
              <!-- feature.description に含まれるトークンも置換 -->
              <div v-html="replaceTokens(feature.description)"></div>
            </template>
          </div>

          <h3 class="second">クチコミ</h3>
          <template v-if="adDetail.custom?.detail?.review_style == 'carousel'">
            <div class="nosp">
              <Carousel :wrapAround="true" :itemsToShow="1.8">
                <Slide v-for="(review, i) in site.reviews" :key="i">
                  <div class="carousel-review-item">
                    <div class="avatar">
                      <img :src="review.icon" :alt="`レビュー#${i+1}`">
                    </div>
                    <!-- review.comment に含まれるトークンも置換 -->
                    <div v-html="replaceTokens(review.comment)" class="comment"></div>
                  </div>
                </Slide>
                <template #addons>
                  <Pagination />
                </template>
              </Carousel>
            </div>
            <div class="nopc">
              <Carousel :wrapAround="true" :itemsToShow="1.2">
                <Slide v-for="(review, i) in site.reviews" :key="i">
                  <div class="carousel-review-item">
                    <div class="avatar">
                      <img :src="review.icon" :alt="`レビュー#${i+1}`">
                    </div>
                    <!-- review.comment に含まれるトークンも置換 -->
                    <div v-html="replaceTokens(review.comment)" class="comment"></div>
                  </div>
                </Slide>
                <template #addons>
                  <Pagination />
                </template>
              </Carousel>
            </div>
          </template>
          <template v-else>
            <ul class="reviews">
              <li v-for="(review, i) in site.reviews" :key="review.icon">
                <div class="avatar">
                  <img :src="review.icon" :alt="`レビュー#${i+1}`">
                </div>
                <!-- review.comment に含まれるトークンも置換 -->
                <div class="comment" v-html="replaceTokens(review.comment)"></div>
              </li>
            </ul>
          </template>
        </div>

        <div class="regist animate__animated animate__pulse animate__infinite">
          <a :href="site.url" target="_blank" >
            <img :src="adDetail.conversion_button_image" :alt="adDetail.sitename">
          </a>
        </div>
      </article>
    </section>

    <!-- フッター -->
    <!-- adDetail.footer_wordings に含まれるトークンも置換 -->
    <div v-html="replaceTokens(adDetail.footer_wordings)"></div>

    <!-- custom.additional_htmlのレンダリング先 -->
    <div class="additional_html" v-if="adDetail.custom?.additional_html">
      <div v-if="Array.isArray(adDetail.custom?.additional_html)">
        <div v-for="(html, index) in adDetail.custom.additional_html" :key="index" v-html="replaceTokens(html)"></div>
      </div>
      <div v-else-if="adDetail.custom?.additional_html" v-html="replaceTokens(adDetail.custom.additional_html)"></div>
    </div>

  </main>

  <!-- カスタムCSS用のリンク挿入先 -->
  <div id="style"></div>
</template>

<style lang="scss" scoped>
/* ========================================================================
   ■ 共通レイアウト・変数定義
   ======================================================================== */
main.boat {
  --accent-color: rgb(0, 85, 128);
  --accent-color-inverse: white;
  --highlight-color: #dbf4f5;
  --jumpbutton-color: var(--accent-color);
  --jumpbutton-color-inverse: var(--accent-color-inverse);
  --accent-color-darken: rgb(0, 15, 55);
  --accent-light-color: #59c7e8;

  --smallitem-border: 2px solid #ccc;
  --maxwidth: 1280px;
}

main {
  section {
    max-width: var(--maxwidth);
    margin: 0 auto;
  }
}

/* メディアクエリでの表示制御 */
@media (max-width: 768px) {
  .nosp { display: none!important; }
}
@media (min-width: 769px) {
  .nopc { display: none!important; }
}

/* ========================================================================
   ■ ヘッダー・基本ボタン
   ======================================================================== */
header {
  h1 {
    max-width: var(--maxwidth);
    margin: 0 auto;
    text-align: center;
    img { max-width: 100%; }
  }
  > div {
    max-width: var(--maxwidth);
    margin: 1em auto;
    text-align: center;
    padding: 0.5em 1.5em;
    width: 100%;
    overflow: hidden;
    img { max-width: 100%; }
  }
}
.pr-notice {
  position: relative;
  margin:0 auto;
  padding:0;
  overflow:visible;
  width:100%;
  > span {
    position: absolute;
    top: 0;
    right: 0;
    z-index:100;
    opacity:0.5;
  }
}

.image-container {
  position: relative;
  img {
    display: block;
    margin: 0 auto;
    max-width: 100%;
  }
  .clickable-area {
    position: absolute;
    cursor: pointer;
  }
}



/* ========================================================================
   ■ セールスポイント・特徴・レビューなどのコンテンツスタイル
   ======================================================================== */
.salespoints {
  border: var(--smallitem-border);
  border-radius: 0.5em;
  padding: 0.75em;
  h4 {
    font-weight: bold;
    display: inline-block;
    background-color: var(--accent-light-color);
    padding: 0.25em;
    margin: 0;
    line-height: 1em;
  }
  ul { padding-left: 1.25em; margin: 0.25em 0; }
}

.features {
  h4 {
    font-weight: bold;
    color: var(--accent-color);
    margin: 0.1em 0;
  }
  > div { margin-bottom: 1em; }
  p { margin: 0; }
}

.reviews {
  list-style: none;
  padding: 0;
  li {
    display: flex;
    align-items: center;
    border: var(--smallitem-border);
    border-radius: 0.5em;
    margin-bottom: 1em;
    padding: 1em;
    img {
      width: 75px;
      height: 75px;
      border-radius: 50%;
      margin-right: 1em;
      box-sizing: border-box;
      display: block;
      border: var(--smallitem-border);
      aspect-ratio: 1 / 1;
    }
    > div {
      font-weight: bold;
      &:first-child { width: 85px; }
      &:last-child { flex: 1; }
    }
  }
}

.carousel-review-item {
  display: flex;
  align-items: center;
  border: var(--smallitem-border);
  border-radius: 0.5em;
  margin: 1em;
  padding: 1em;
  @media (max-width: 768px) { margin: 1em 0.25em; }
  img {
    width: 75px;
    height: 75px;
    border-radius: 50%;
    margin-right: 1em;
    box-sizing: border-box;
    display: block;
    border: var(--smallitem-border);
    aspect-ratio: 1 / 1;
  }
  > div {
    font-weight: bold;
    &:first-child { width: 85px; }
    &:last-child { flex: 1; }
  }
  .comment { text-align: left; }
}

.accomplishment-tile {
  display: flex;
  list-style: none;
  padding: 0;
  margin: 0;
  flex-wrap: wrap;
  gap: 1em;
  align-items: center;
  justify-content: center;
  li {
    display: inline-block;
    width: calc(50% - 1em);
    margin: 0;
    padding: 0;
    box-sizing: border-box;
    text-align: center;
    @media (max-width: 768px) {
      width: calc(100% - 1em);
      position: relative;
      left: -1em;
    }
  }
}

.accomplishment-item {
  --shadow-length: 4px;
  background-color: var(--accent-color);
  color: var(--accent-color-inverse);
  width: 100%;
  max-width: 640px;
  border-radius: 1em;
  box-shadow: 0 var(--shadow-length) 0 rgba(0, 0, 0, 0.25);
  margin: 0 var(--shadow-length) var(--shadow-length) var(--shadow-length);
  h4 { position: relative; top: -0.5em; font-weight: bold; }
  .race-name { font-weight: bold; margin-bottom: 0.5em; }
  .result {
    color: red;
    font-size: 200%;
    @media (max-width: 768px) {
      font-size: 180% !important;
      .unit { font-size: 70%; }
    }
  }
  > div {
    background-color: #fff;
    color: #333;
    padding: 1em 0.5em 1.5em 0.5em;
    border-radius: 0.5em;
  }
  padding: 1em;
}

/* ========================================================================
   ■ 見出しとサイト詳細
   ======================================================================== */
h3.first {
  background-color: var(--accent-light-color);
  text-align: center;
  font-weight: bold;
  padding: 0.5em 0;
  border-radius: 0.5em;
  margin-bottom: 0.5em;
}
h3.second {
  border-left: 0.25em solid var(--accent-color);
  padding: 0.25em 0.25em 0.25em 0.6em;
  font-weight: bold;
  margin: 1.5em 0 1em 0;
  background-color: var(--highlight-color);
  a { font-weight: inherit; }
}

.site-detail {
  article {
    background-color: var(--highlight-color);
    padding: 1em;
    margin: 2em 0;
    h2 {
      text-align: center;
      font-weight: 900;
      background-color: var(--accent-color);
      color: var(--accent-color-inverse);
      padding: 0.5em 0;
      border-radius: 0.5em;
      margin-bottom: 0.5em;
      .rank-icon {
        display: inline-block;
        width: 1em;
        height: 1em;
        margin-right: 0.5em;
        transform: scale(2.5);
      }
    }
    > div {
      background-color: #fff;
      padding: 1em;
      border-radius: 1em;
      &.regist {
        background-color: transparent;
        text-align: center;
        > a {
          cursor: pointer;
          &:hover { transform: scale(1.05); }
        }
        img {
          max-width: 90%;
          cursor: pointer;
          &:hover { transform: scale(1.05); }
        }
      }
    }
  }
}
.site-detail--lead {
  display: flex;
  width: 100%;
  gap: 1em;
  @media (max-width: 768px) { flex-direction: column; }
  img { max-width: 100%; }
  > div {
    &:first-child {
      width: 67%;
      @media (max-width: 768px) { width: 100%; }
    }
    &:last-child {
      width: 33%;
      @media (max-width: 768px) { width: 100%; }
    }
  }
  h3.first { margin-bottom: 0; border-radius: 0.5em 0.5em 0 0; }
  .score {
    background-color: var(--highlight-color);
    padding: 1.5em 0.5em 1em 0.5em;
    border-radius: 0 0 0.5em 0.5em;
    text-align: center;
    font-weight: bold;
    margin-bottom: 1em;
    strong { font-size: 270%; color: red; }
    > small { font-size: 90%; font-weight: bold; }
  }
}

/* ========================================================================
   ■ テーブル（水平・スクロール）
   ======================================================================== */
.horizontal-table {
  margin: 0 auto;
  .rank-icon {
    --size: 6em;
    width: var(--size);
    height: var(--size);
    margin: calc(var(--size) * -1);
    left: -2em;
    top: -1em;
    position: absolute;
    z-index: 2;
    background-repeat: no-repeat;
    &-1st { /* background-color 等、必要に応じ設定 */ }
    &-2nd { }
    &-3rd { }
  }
  table {
    font-size: 80%;
    line-height: 1.5em;
    min-width: 1000px;
    --basewidth: 8%;
    --prime-width: 12%;
    thead {
      img {
        display: block;
        margin: 0 auto;
        width: 95%;
        border-radius: 0.25em;
        border: 1px solid #aaa;
        box-sizing: border-box;
      }
      th { position: relative; padding-top: 2em; }
      th, td {
        position: relative;
      }
      a.chip {
        display: inline-block;
        background-color: #fff;
        color: #333;
        border-radius: 1em;
        padding: 0.4em 0.75em;
        font-size: 90%;
        line-height: 1em;
        position: relative;
        top: -2.5em;
        text-decoration: none;
        font-weight: bold;
        box-shadow: 0 1px 0 #000;
      }
    }
    tbody th {
      font-weight: bold;
      background-color: var(--accent-color);
      color: var(--accent-color-inverse);
      position: sticky;
      left: 0;
      z-index: 10;
      padding: 0.5em 0;
      & > span {
        padding: 2em 1em;
        display: inline-block;
        min-width: 200px;
        font-weight: bold;
        @media (max-width: 768px) {
          min-width: 0;
          padding: 1em 0.5em;
        }
      }
    }
    th, td {
      text-align: center;
      min-width: 140px;
      max-width: 500px;
      position: relative;
      &.prime {
        font-weight: 900; min-width: 160px;
      }

    }
    .stars { padding-bottom: 0.5em; }
    .ui-scoring-icon {
      --size: 3.5em;
      margin: calc(var(--size) * -0.5);
      left: 50%;
      top: 50%;
      position: absolute;
    }
    td {
      padding: 1rem 1.5rem;
      font-size: 120%;
    }
    td.accomplishments {
      font-weight: bold;
      &.prime { color: red; }
    }
    td.score {
      font-size: 150%;
      font-family: Arial, Helvetica, sans-serif;
      &.prime { color: red; }
    }
    tr > td {
      border-right: #ccc 1px solid;
      border-bottom: #ccc 1px solid;
      &:last-child { border-right: none; }
    }
    tr:last-child > td { border-bottom: none; }
    tbody {
      td.prime { background-color: var(--highlight-color) }
    }
  }
  > div {
    background-color: #fff;
    border-radius: 1em;
    padding: 1em;
    overflow-x: auto;
  }
  .rank-icon {
    left: 4em;
    top: 6em;
  }
}

.scrollable-table {
  margin: 0 auto;
  background-color: var(--accent-color);
  padding: 1em;
  table {
    font-size: 80%;
    line-height: 1.5em;
    min-width: 1000px;
    .rank-icon {
      --size: 5em;
      width: var(--size);
      height: var(--size);
      margin: calc(var(--size) * -1);
      left: 4em;
      top: 4em;
      position: absolute;
      z-index: 2;
      display: inline-block;
      background-repeat: no-repeat;
      &-1st { }
      &-2nd { }
      &-3rd { }
    }
    th.prime .rank-icon { left: 3.5em;
      top: 4em; }
    --basewidth: 8%;
    --prime-width: 12%;
    thead {
      img {
        display: block;
        margin: 0 auto;
        width: 95%;
        border-radius: 0.25em;
        border: 1px solid #aaa;
        box-sizing: border-box;
      }
      a.chip {
        display: inline-block;
        background-color: #999;
        color: #fff;
        border-radius: 1em;
        padding: 0.4em 0.75em;
        font-size: 90%;
        line-height: 1em;
        margin-top: 0.5em;
        text-decoration: none;
      }
      th { width: var(--basewidth); position: relative; }
      th.prime { width: var(--prime-width); max-width: 200px; }
    }
    tbody th {
      font-weight: bold;
      background-color: var(--accent-color);
      color: var(--accent-color-inverse);
      position: relative;
      padding: 0.5em 0;
      > span.centering {
        --title-width: calc(100vw - 40px);
        --title-maxwidth: calc(var(--maxwidth) - 2em);
        display: block;
        width: 300px;
        margin: 0 -150px;
        position: sticky;
        left: calc((var(--title-width) - 300px) * 0.5);
        @media (min-width: 1280px) {
          left: calc(300px * -0.5 + 50%);
        }
        > span { font-weight: bold; }
      }
    }
    th, td {
      text-align: center;
      min-width: 140px;
      max-width: 200px;
      @media (max-width: 768px) {
        max-width: 180px;
        min-width: 140px;
      }
    }
    td { padding: 1rem 1.5rem; width: var(--basewidth); }
    td.prime {
      background-color: var(--highlight-color);
      width: var(--prime-width);
      max-width: 220px;
      min-width: 180px;
      @media (max-width: 768px) {
        max-width: 180px;
        min-width: 180px;
      }
    }
    td.accomplishments {
      font-weight: bold;
      &.prime { color: red; }
    }
    td.score {
      font-size: 150%;
      font-family: Arial, Helvetica, sans-serif;
      &.prime { color: red; font-weight: 900; }
    }
    tr > td {
      border-right: #ccc 1px solid;
      &:last-child { border-right: none; }
    }
  }
  > div {
    background-color: #fff;
    border-radius: 1em;
    padding: 1em;
    overflow-x: auto;
  }
  &.bordered {
    background-color: transparent;
    > div {
      border: 4px solid var(--accent-color);
      tbody th {
        background-color: transparent;
        color: var(--accent-color);
        font-weight: bold;
        position: relative;
        &:before {
          content: "";
          position: absolute;
          left: 0;
          bottom: 0.5em;
          width: 100%;
          border-bottom: 2px solid var(--accent-color);
        }
      }
      thead {
        img { border-radius: 1em; }
      }
    }
  }
  &.rounded {
    border-radius: 2em;
    > div {
      tbody {
        th { background-color: transparent; }
        td {
          background-color: rgb(241, 241, 241);
          border-radius: 1em;
        }
        td.prime { background-color: #fbffbe; }
        tr { margin-bottom: 1em; }
      }
    }
    .table-labeltext {
      display: inline-block;
      background-color: var(--accent-color);
      padding: 0.5em 1em;
      min-width: 200px;
      border-radius: 2em;
      margin-top: 0.25em;
    }
  }
}

/* ========================================================================
   ■ 詳細スコアテーブル（サイト詳細内）
   ======================================================================== */
.detail-score-table {
  width: 100%;
  th,
  td {
    width: 50%;
    border-bottom: 1px solid #ccc;
    padding: 0.5em 0;
  }
  th { text-align: right; font-weight: bold; }
  td { text-align: center; font-size: 150%; }
}

/* ========================================================================
   ■ ランクアイコン共通設定（背景画像）
   ======================================================================== */
.rank-icon {
  background-size: contain;
  background-repeat: no-repeat;
  background-position: center;
  &.rank-icon-1st { background-image: url(../assets/rank1.png); }
  &.rank-icon-2nd { background-image: url(../assets/rank2.png); }
  &.rank-icon-3rd { background-image: url(../assets/rank3.png); }
}
</style>
